import React, { Component, lazy, Suspense } from "react";
import { connect } from "react-redux";
import { withRouter, Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import PpvControlMiddleware from "../../../commons/ppv-middleware";
import clientPropType from "../../../prop-types/client";
import eventStreamPropType from "../../../prop-types/eventStream";
import "../styles/presto-player.scss";
import {
  EVENTS,
  ON_DEMAND,
  SCHEDULED,
  PLANNED,
  INFO
} from "../../../constants";
import { postConsumeEvent } from "../../../state/user/actions";
import { recordStreamActivity } from "../../../state/events/actions";
import { setInfoMessage } from "../../../state/info-message/actions";
import { Spinner } from "../../../commons/elements";

// React 16.6.0: Introduction of React.lazy and Suspense
// lazy lets you defer loading component’s code until it is rendered for the first time.
const VideoPlayer = lazy(() => import("../components/stream-player"));
const Theoplayer = lazy(() => import("../components/presto-player"));

const propTypes = {
  filters: PropTypes.shape({
    clientId: PropTypes.number,
    sport: PropTypes.number,
    tabName: PropTypes.string,
    streamType: PropTypes.string
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      shortName: PropTypes.string.isRequired,
      eventId: PropTypes.string.isRequired,
      streamType: PropTypes.string.isRequired
    })
  }).isRequired,
  // Map state to props
  event: eventStreamPropType.isRequired,
  client: clientPropType.isRequired,
  accountId: PropTypes.number.isRequired,
  // Map dispatch to props
  recordStreamActivityAction: PropTypes.func.isRequired,
  postConsumeEventAction: PropTypes.func.isRequired,
  setInfoMessageAction: PropTypes.func.isRequired
};

class PrestoPlayerContainer extends Component {
  static getTab = (tabName, status) => {
    let tab = tabName;
    if (!tabName) {
      if (status === "on-demand") {
        tab = ON_DEMAND;
      } else {
        tab = SCHEDULED;
      }
    }
    return tab;
  };

  onBack = () => {
    const {
      event: { status },
      filters: { tabName },
      history,
      location,
      match: {
        params: { shortName }
      }
    } = this.props;
    const tab = PrestoPlayerContainer.getTab(tabName, status);
    if (tab === "network") {
      history.push(`/${shortName}/${tab}${location.search}`);
    } else {
      history.push(`/${shortName}/${EVENTS}/${tab}${location.search}`);
    }
  };

  render() {
    const {
      filters: { tabName },
      client,
      event,
      accountId,
      recordStreamActivityAction,
      postConsumeEventAction,
      setInfoMessageAction,
      match: {
        params: { shortName }
      },
      location
    } = this.props;

    let infoPage;
    if (event.status === PLANNED) {
      const startTime = moment(event.startTime).format("LT [on] L");
      let message = "";
      if (event.ppv && event.streamType === "video") {
        message = "You have already purchased this event. ";
      }
      message += `This event will begin at ${startTime}`;

      setInfoMessageAction({
        message,
        tab: PrestoPlayerContainer.getTab(tabName, event.status)
      });

      infoPage = `/${shortName}/${INFO}${location.search}`;
    }

    if (client.features?.useVideoJS) {
      return (
        <PpvControlMiddleware>
          {event.status === PLANNED ? (
            <Redirect to={infoPage} />
          ) : (
            // <Suspense> lets you display a fallback until its children have finished loading
            <Suspense
              fallback={
                <div className="d-flex justify-content-center align-items-center h-100 w-100">
                  <Spinner />
                </div>
              }
            >
              <VideoPlayer
                client={client}
                event={event}
                tabName={tabName}
                recordStreamActivity={recordStreamActivityAction}
                accountId={accountId}
              />
            </Suspense>
          )}
        </PpvControlMiddleware>
      );
    }

    return (
      <PpvControlMiddleware>
        {event.status === PLANNED ? (
          <Redirect to={infoPage} />
        ) : (
          // <Suspense> lets you display a fallback until its children have finished loading
          <Suspense
            fallback={
              <div className="d-flex justify-content-center align-items-center h-100 w-100">
                <Spinner />
              </div>
            }
          >
            <Theoplayer
              url={event?.url}
              evtItem={event}
              client={client}
              onBack={this.onBack}
              postConsumeEvent={postConsumeEventAction}
              accountId={accountId}
            />
          </Suspense>
        )}
      </PpvControlMiddleware>
    );
  }
}

PrestoPlayerContainer.propTypes = propTypes;

const mapStateToProps = (state) => ({
  client: state.client.client,
  event: state.events.events.selected.data,
  accountId: state.user.user.id || 0
});

const mapDispatchToProps = {
  recordStreamActivityAction: recordStreamActivity,
  postConsumeEventAction: postConsumeEvent,
  setInfoMessageAction: setInfoMessage
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PrestoPlayerContainer)
);
